import { useState, useEffect } from "react";
import { AlertTriangle, Eye, EyeOff, Lock, Mail, ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { useAuth } from "../services/AuthService";
import { useComingSoon } from "../utils/useComingSoon";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, clearError } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showComingSoon } = useComingSoon();

  const searchParams = new URLSearchParams(location.search);
  const reason = searchParams.get("reason") || null;
  const isSessionExpired = reason === "session_expired";
  const nextPath = searchParams.get("next") || "/dashboard";

  useEffect(() => {
    window.scrollTo(0, 0);
    clearError();
  }, [clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showNotification({
        type: "warning",
        title: "Missing Fields",
        message: "Please fill in all required fields."
      });
      return;
    }

    const result = await login(email, password);
    if (result.status === "success") {
      showNotification({
        type: "success",
        title: "Welcome Back",
        message: "Successfully signed in."
      });
      navigate(nextPath);
    } else {
      showNotification({
        type: "failed",
        title: "Login Failed",
        message: result.message || "Invalid email or password."
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO title="Account Sign In" url="/login" />
      <JazzHeader />

      <main className="flex-1 flex items-center justify-center p-4 py-12 md:py-20 bg-[#fafafa] relative overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-jazz-brown/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-jazz-brown/5 rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-md relative animate-fade-in">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-black/60 hover:text-jazz-brown mb-8 font-bold text-[10px] tracking-[2px] uppercase transition-all group"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Home</span>
          </Link>

          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-white border border-black/5 flex items-center justify-center shadow-sm mx-auto mb-6 rounded-2xl">
              <img src="/static/images/temb_music_logo.png" alt="Temidayo Balogun Logo" className="w-12 h-12 object-contain" />
            </div>
            <h1 className="text-4xl font-serif italic text-black mb-2">
              Welcome Back
            </h1>
            <p className="text-xs font-bold tracking-[1px] uppercase text-black/60">
              Sign in to manage your account
            </p>
          </div>

          {/* Session Banner */}
          {isSessionExpired && (
            <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 animate-fade-in">
              <AlertTriangle className="text-amber-500 mt-0.5 shrink-0" size={18} />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Session Expired</p>
                <p className="text-xs text-amber-600">Please sign in again to continue.</p>
              </div>
            </div>
          )}

          {/* Login Card */}
          <div className="bg-white rounded-[2rem] shadow-xl border border-black/5 p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Email */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold tracking-[2px] uppercase text-black/60 ml-1">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 group-focus-within:text-jazz-brown transition-colors" size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-[#fafafa] border border-black/5 rounded-xl text-sm font-semibold text-black placeholder-black/40 focus:outline-none focus:border-jazz-brown transition-all"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold tracking-[2px] uppercase text-black/60 ml-1">
                  Password
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 group-focus-within:text-jazz-brown transition-colors" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 bg-[#fafafa] border border-black/5 rounded-xl text-sm font-semibold text-black placeholder-black/40 focus:outline-none focus:border-jazz-brown transition-all"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-jazz-brown transition-colors p-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="flex items-center justify-between px-1">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-black/10 text-jazz-brown focus:ring-jazz-brown bg-[#fafafa]"
                  />
                  <span className="text-[10px] font-bold tracking-[1px] uppercase text-black/60 group-hover:text-black transition-colors">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[10px] font-bold tracking-[1px] uppercase text-jazz-brown hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 px-6 bg-black text-white font-bold text-[10px] tracking-[3px] uppercase rounded-xl shadow-lg hover:bg-jazz-brown transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <div className="mt-8 mb-8 flex items-center gap-4">
              <div className="flex-1 border-t border-black/5"></div>
              <span className="text-[10px] font-bold tracking-[2px] uppercase text-black/40">or</span>
              <div className="flex-1 border-t border-black/5"></div>
            </div>

            <button
              type="button"
              onClick={() => showComingSoon({ title: "Google Login", message: "We're currently setting up secure Google authentication." })}
              className="w-full py-5 px-6 bg-white border border-black/10 text-black font-bold text-[10px] tracking-[3px] uppercase rounded-xl hover:bg-[#fafafa] hover:border-black/20 transform hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>

            {/* Registration Promise */}
            <div className="mt-8 pt-8 border-t border-black/5 text-center">
              <p className="text-black/60 text-[10px] font-bold tracking-[1px] uppercase">
                Don't have an account?{" "}
                <Link
                  to="/contact"
                  className="text-jazz-brown hover:underline"
                >
                  Contact us to register
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center text-[9px] font-bold tracking-[2px] uppercase text-black/40 mt-12 leading-relaxed">
            &copy; {new Date().getFullYear()} TEMB Music. All rights reserved.
          </p>
        </div>
      </main>

      <JazzFooter />

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
      `}</style>
    </div>
  );
}
